import { Box, Checkbox, Flex, Text } from '@chakra-ui/react';
import { IRowNode } from '@ag-grid-community/core';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { NormalizedColumn, ResponsiveCardRenderer, ResponsiveCardViewOptions, ResponsiveGridState } from './types';
import { resolveFieldValue } from './resolveFieldValue';
import { buildResolvedFields, sortFields } from './fieldUtils';
import { DefaultCardRenderer } from './DefaultCardRenderer';
import { EmptyState } from '@/components/ui/empty-state';
import { CardSelectionApi, useCardSelection } from './useCardSelection';

type CardListViewProps<TData, TColId extends string> = {
    columns: NormalizedColumn<TData, TColId>[];
    rowData: TData[];
    state: ResponsiveGridState;
    cardView?: ResponsiveCardViewOptions<TData, TColId>;
    rowSelection?: 'single' | 'multiple';
    suppressRowClickSelection?: boolean;
    isRowSelectable?: (params: { data: TData | undefined }) => boolean;
    onSelectionChanged?: () => void;
    selectionRef?: MutableRefObject<CardSelectionApi<TData> | null>;
    doesExternalFilterPass?: (node: IRowNode<TData>) => boolean;
    isExternalFilterPresent?: () => boolean;
    onRowClicked?: (row: TData, rowIndex: number) => void;
    onRowSelected?: (row: TData, rowIndex: number, selected: boolean) => void;
    onActiveRowCountChange?: (count: number) => void;
};

/** @internal exported for testing */
export function applyClientSideFilters<TData, TColId extends string>(
    rows: TData[],
    columns: NormalizedColumn<TData, TColId>[],
    state: ResponsiveGridState,
    doesExternalFilterPass?: (node: IRowNode<TData>) => boolean,
    isExternalFilterPresent?: () => boolean,
): TData[] {
    let filtered = rows;

    // Apply internal card-mode filters from filterModel
    const filterEntries = Object.entries(state.filterModel);
    if (filterEntries.length > 0) {
        filtered = filtered.filter((row, idx) => {
            return filterEntries.every(([colId, filterValue]) => {
                if (!filterValue || typeof filterValue !== 'object') {
                    return true;
                }

                const col = columns.find((c) => c.colId === colId);
                if (!col) {
                    return true;
                }

                const { rawValue, displayValue } = resolveFieldValue(col.colDef, row, idx);
                const rec = filterValue as Record<string, unknown>;

                if ('filter' in rec && typeof rec.filter === 'string' && rec.filter) {
                    const searchStr = rec.filter.toLowerCase();
                    const displayStr = displayValue != null ? String(displayValue).toLowerCase() : '';
                    const rawStr = rawValue != null ? String(rawValue).toLowerCase() : '';
                    return displayStr.includes(searchStr) || rawStr.includes(searchStr);
                }

                if ('filter' in rec && typeof rec.filter === 'number') {
                    return rawValue !== null && rawValue !== undefined && Number(rawValue) === rec.filter;
                }

                if ('dateFrom' in rec && typeof rec.dateFrom === 'string' && rec.dateFrom) {
                    const raw = rawValue != null ? String(rawValue) : '';
                    return raw.includes(rec.dateFrom);
                }

                if ('value' in rec && typeof rec.value === 'string' && rec.value) {
                    const raw = rawValue != null ? String(rawValue).toLowerCase() : '';
                    return raw === rec.value.toLowerCase();
                }

                if ('values' in rec && Array.isArray(rec.values) && rec.values.length > 0) {
                    const selected = (rec.values as unknown[]).map(String);

                    // Prefer matching by id when the row carries one — some list DTOs only
                    // expose the display name, so two options sharing a name would otherwise
                    // be indistinguishable (mirrors BranchFilter.doesFilterPass).
                    const rowRecord = row as Record<string, unknown>;
                    if (typeof rowRecord.branchId === 'string') {
                        return selected.includes(rowRecord.branchId);
                    }

                    const raw = rawValue != null ? String(rawValue) : '';
                    const filterParams = col.colDef.filterParams as { branches?: { id: string; name: string }[] } | undefined;
                    const selectedNames = filterParams?.branches
                        ?.filter((branch) => selected.includes(branch.id))
                        .map((branch) => branch.name);

                    return selectedNames ? selectedNames.includes(raw) : selected.includes(raw);
                }

                return true;
            });
        });
    }

    // Apply external filter
    if (isExternalFilterPresent && doesExternalFilterPass && isExternalFilterPresent()) {
        filtered = filtered.filter((row) => {
            const nodeShim = { data: row } as IRowNode<TData>;
            return doesExternalFilterPass(nodeShim);
        });
    }

    return filtered;
}

/** @internal exported for testing */
export function applyClientSideSort<TData, TColId extends string>(
    rows: TData[],
    columns: NormalizedColumn<TData, TColId>[],
    state: ResponsiveGridState,
): TData[] {
    if (state.sortModel.length === 0) {
        return rows;
    }

    const { colId, sort } = state.sortModel[0];
    const col = columns.find((c) => c.colId === colId);
    if (!col) {
        return rows;
    }

    const direction = sort === 'desc' ? -1 : 1;

    // Build a map of each row to its original index so valueGetters
    // receive a meaningful rowIndex / node.id during comparison.
    const indexMap = new Map<TData, number>();
    for (let i = 0; i < rows.length; i++) {
        indexMap.set(rows[i], i);
    }

    return [...rows].sort((a, b) => {
        const aVal = resolveFieldValue(col.colDef, a, indexMap.get(a) ?? 0).rawValue;
        const bVal = resolveFieldValue(col.colDef, b, indexMap.get(b) ?? 0).rawValue;

        if (aVal == null && bVal == null) {
            return 0;
        }
        if (aVal == null) {
            return direction;
        }
        if (bVal == null) {
            return -direction;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return direction * aVal.localeCompare(bVal);
        }
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return direction * (aVal - bVal);
        }

        return direction * String(aVal).localeCompare(String(bVal));
    });
}

export function CardListView<TData, TColId extends string>({
    columns,
    rowData,
    state,
    cardView,
    rowSelection,
    suppressRowClickSelection,
    isRowSelectable,
    onSelectionChanged,
    selectionRef,
    doesExternalFilterPass,
    isExternalFilterPresent,
    onRowClicked,
    onRowSelected,
    onActiveRowCountChange,
}: CardListViewProps<TData, TColId>) {
    const CardRenderer = (cardView?.renderCard ?? DefaultCardRenderer) as ResponsiveCardRenderer<TData, TColId>;
    const EmptyComponent = cardView?.renderEmptyState;

    const layoutSlots = cardView?.layout?.slots;
    const layoutOrder = cardView?.layout?.order;
    const layoutHidden = cardView?.layout?.hidden;

    const processedRows = useMemo(() => {
        const filtered = applyClientSideFilters(rowData, columns, state, doesExternalFilterPass, isExternalFilterPresent);
        return applyClientSideSort(filtered, columns, state);
    }, [rowData, columns, state, doesExternalFilterPass, isExternalFilterPresent]);

    useEffect(() => {
        onActiveRowCountChange?.(processedRows.length);
    }, [processedRows.length, onActiveRowCountChange]);

    const selection = useCardSelection<TData>({
        rowSelection,
        isRowSelectable,
        rows: processedRows,
        onSelectionChanged,
    });

    // Expose selection API to parent via ref
    if (selectionRef) {
        selectionRef.current = selection;
    }

    const { deselectAll } = selection;
    const prevStateRef = useRef(state);
    const prevRowDataRef = useRef(rowData);
    useEffect(() => {
        const stateChanged = prevStateRef.current !== state;
        const dataChanged = prevRowDataRef.current !== rowData;
        prevStateRef.current = state;
        prevRowDataRef.current = rowData;
        if (stateChanged || dataChanged) {
            deselectAll();
        }
    }, [state, rowData, deselectAll]);

    const handleSelect = useCallback(
        (row: TData, rowIndex: number) => {
            selection.toggleSelection(row, rowIndex);
        },
        [selection],
    );

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const overscan = cardView?.overscan ?? 5;

    const virtualizer = useVirtualizer({
        count: processedRows.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: () => 80,
        gap: 8,
        overscan,
    });

    const selectableCount = useMemo(() => {
        if (!isRowSelectable) {
            return processedRows.length;
        }
        return processedRows.filter((row) => isRowSelectable({ data: row })).length;
    }, [processedRows, isRowSelectable]);

    if (processedRows.length === 0) {
        if (EmptyComponent) {
            return <EmptyComponent />;
        }
        return <EmptyState title="No results found" description="Try adjusting your filters" />;
    }

    const allSelected = selectableCount > 0 && selection.selectedIndices.size === selectableCount;
    const someSelected = selection.selectedIndices.size > 0 && !allSelected;

    return (
        <Flex flexDirection="column" overflow="hidden" h="100%">
            {rowSelection === 'multiple' && processedRows.length > 0 && (
                <Flex alignItems="center" gap="8px" px="12px" py="6px" flexShrink={0}>
                    <Checkbox.Root
                        checked={someSelected ? 'indeterminate' : allSelected}
                        onCheckedChange={(e) => {
                            if (e.checked) {
                                selection.selectAll();
                            } else {
                                selection.deselectAll();
                            }
                        }}
                        size="sm">
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                    </Checkbox.Root>
                    <Text fontSize="sm" color="fg.muted">
                        {selection.selectedIndices.size > 0
                            ? `${selection.selectedIndices.size} of ${selectableCount} selected`
                            : `Select all (${selectableCount})`}
                    </Text>
                </Flex>
            )}
            <Box ref={scrollContainerRef} overflow="auto" flex="1">
                <Box position="relative" w="100%" style={{ height: `${virtualizer.getTotalSize()}px` }}>
                    {virtualizer.getVirtualItems().map((virtualItem) => {
                        const idx = virtualItem.index;
                        const row = processedRows[idx];
                        const allFields = buildResolvedFields(columns, row, idx, layoutSlots, layoutHidden);
                        const orderedFields = sortFields(allFields, layoutOrder);
                        const visibleFields = orderedFields.filter((f) => !f.hidden && f.slot !== 'hidden');

                        const getField = (colId: TColId) => allFields.find((f) => f.colId === colId);

                        return (
                            <Box
                                key={virtualItem.key}
                                ref={virtualizer.measureElement}
                                data-index={idx}
                                position="absolute"
                                top={0}
                                left={0}
                                w="100%"
                                style={{ transform: `translateY(${virtualItem.start}px)` }}>
                                <CardRenderer
                                    row={row}
                                    rowIndex={idx}
                                    isSelected={selection.isSelected(idx)}
                                    isSelectable={selection.isSelectable(row)}
                                    selectionMode={rowSelection}
                                    fields={orderedFields}
                                    visibleFields={visibleFields}
                                    getField={getField}
                                    onSelect={() => {
                                        const willBeSelected = !selection.isSelected(idx);
                                        if (!suppressRowClickSelection) {
                                            handleSelect(row, idx);
                                            onRowSelected?.(row, idx, willBeSelected);
                                        }
                                        onRowClicked?.(row, idx);
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Flex>
    );
}
